import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Radio } from '@components/core-ui/form-elements/Radio';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { jobStatusCanBeChanged } from '@helpers/jobStatus/jobStatusCanBeChanged';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import {
  GetOrgMembersMemberResponseDto,
  JobDtoUser,
  JobResponseDto,
  JobsService,
  OrganizationsService,
  OrganizationTeamResponseDto,
  OrganizationTeamWithMembersResponseDto,
  WorkspacesService,
} from 'app/API';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import { debounce } from 'lodash-es';
import React, { useEffect } from 'react';
import { Content } from './AssignJobModal/styles/Content';
import { StyledTextarea } from './AssignJobModal/styles/StyledTextarea';
import TeamEmptyWarningModal from './components/TeamsEmptyWarningModal';
import TeamsFilter from './components/TeamsFilter';
import TeamTag from './components/TeamTag';

type TProps = {
  job: JobResponseDto;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AssignJobModal = ({ job, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [users, setUsers] = React.useState<GetOrgMembersMemberResponseDto[]>([]);
  const { organization } = useAuthContext();
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<GetOrgMembersMemberResponseDto | JobDtoUser | undefined>(job.assignee);
  const [status, setStatus] = React.useState<string>(
    job.assignee ? 'JOB_STATUS_TRANSCRIBING' : 'JOB_STATUS_READY_FOR_TRANSCRIBING',
  );
  const [comment, setComment] = React.useState('');

  const [chosenTeamsFilter, setChosenTeamsFilter] = React.useState<string[]>([]);
  const [teamsFilterValues, setTeamsFilterValues] = React.useState<
    (OrganizationTeamResponseDto | OrganizationTeamWithMembersResponseDto)[]
  >([]);
  const [isFetchingData, setIsFetchingData] = React.useState<boolean>(false);

  const canChangeStatus = jobStatusCanBeChanged(job.status);

  const workspace = (organization?.workspaces ?? []).find((ws) => ws.id === job.workspaceId);

  const userHasManageMembersPermissionForCurrentWorkspace = (organization?.userTeams ?? []).some(
    (userTeam) =>
      userTeam.manageMembers && (workspace?.Teams ?? []).some((workspaceTeam) => workspaceTeam.slug === userTeam.slug),
  );

  useEffect(() => {
    setIsFetchingData(true);
    const debouncedSearch = debounce(() => {
      if (userHasManageMembersPermissionForCurrentWorkspace) {
        call(
          OrganizationsService.getOrgMembers({
            oid: String(organization?.id),
            pageNo: '1',
            pageSize: '20',
            orderBy: '',
            orderType: '',
            search,
            teamsSlugFilter: chosenTeamsFilter.join('|'),
          }),
        )
          .then((result) => setUsers(result.data))
          .finally(() => setIsFetchingData(false));
        return;
      }

      call(
        OrganizationsService.getWorkspaceMembers({
          oid: String(organization?.id),
          workspaceId: String(workspace?.id),
          search,
          teamsSlugFilter: chosenTeamsFilter.join('|'),
        }),
      )
        .then((result) => setUsers(result))
        .finally(() => setIsFetchingData(false));
    }, 500);

    debouncedSearch();

    return () => debouncedSearch.cancel();
  }, [search, chosenTeamsFilter, userHasManageMembersPermissionForCurrentWorkspace]);

  useEffect(() => {
    setIsFetchingData(true);
    const getTeams = () => {
      if (!organization?.slug || !workspace?.slug) {
        return;
      }

      if (userHasManageMembersPermissionForCurrentWorkspace) {
        call(OrganizationsService.getOrgTeams({ oid: organization.id }))
          .then((response) => setTeamsFilterValues(response))
          .finally(() => setIsFetchingData(false));
        return;
      }

      call(WorkspacesService.getTeams({ orgSlug: organization?.slug, wsSlug: workspace?.slug }))
        .then((response) => setTeamsFilterValues(response.result))
        .finally(() => setIsFetchingData(false));
    };

    getTeams();
  }, [organization, workspace, userHasManageMembersPermissionForCurrentWorkspace]);

  const changeAssignee = (data: GetOrgMembersMemberResponseDto | null) => {
    if (!data) {
      setAssignee(undefined);
      setStatus('JOB_STATUS_READY_FOR_TRANSCRIBING');
      return;
    }
    setAssignee(data);
    setStatus('JOB_STATUS_TRANSCRIBING');
  };

  const renderStatusRadioList = () => {
    if (!assignee) {
      return (
        <div>
          <div className="radioWrapper">
            <Radio
              onChange={() => setStatus('JOB_STATUS_READY_FOR_TRANSCRIBING')}
              checked={status === 'JOB_STATUS_READY_FOR_TRANSCRIBING'}
            >
              {JOB_STATUSES_ENUM.JOB_STATUS_READY_FOR_TRANSCRIBING}
            </Radio>
          </div>
          <div className="radioWrapper">
            <Radio
              onChange={() => setStatus('JOB_STATUS_READY_FOR_PROOFREADING')}
              checked={status === 'JOB_STATUS_READY_FOR_PROOFREADING'}
            >
              {JOB_STATUSES_ENUM.JOB_STATUS_READY_FOR_PROOFREADING}
            </Radio>
          </div>
          <div className="radioWrapper">
            <Radio
              onChange={() => setStatus('JOB_STATUS_READY_FOR_FINAL')}
              checked={status === 'JOB_STATUS_READY_FOR_FINAL'}
            >
              {JOB_STATUSES_ENUM.JOB_STATUS_READY_FOR_FINAL}
            </Radio>
          </div>
          <div className="radioWrapper">
            <Radio onChange={() => setStatus('JOB_STATUS_COMPLETE')} checked={status === 'JOB_STATUS_COMPLETE'}>
              {JOB_STATUSES_ENUM.JOB_STATUS_COMPLETE}
            </Radio>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="radioWrapper">
          <Radio onChange={() => setStatus('JOB_STATUS_TRANSCRIBING')} checked={status === 'JOB_STATUS_TRANSCRIBING'}>
            {JOB_STATUSES_ENUM.JOB_STATUS_TRANSCRIBING}
          </Radio>
        </div>
        <div className="radioWrapper">
          <Radio onChange={() => setStatus('JOB_STATUS_PROOFING')} checked={status === 'JOB_STATUS_PROOFING'}>
            {JOB_STATUSES_ENUM.JOB_STATUS_PROOFING}
          </Radio>
        </div>
        <div className="radioWrapper">
          <Radio onChange={() => setStatus('Leave as it is')} checked={status === 'Leave as it is'}>
            Leave as it is
          </Radio>
        </div>
      </div>
    );
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const handleSubmit = async () => {
    const workspaceId = job.workspaceId ?? workspace?.id ?? '';
    let newStatus = status === 'Leave as it is' ? job.status : status;
    if (!canChangeStatus) {
      newStatus = job.status;
    }
    await call(
      JobsService.reassignJob({
        wsid: workspaceId,
        jobId: job.id,
        requestBody: {
          assigneeId: assignee?.id,
          status: newStatus as keyof typeof JOB_STATUSES_ENUM,
          assigneeComment: comment,
        },
      }),
    );
    onSuccess();
  };

  return (
    <ModalWrapper width="550px">
      <ModalTitle>Edit assignee</ModalTitle>
      <Content>
        {!teamsFilterValues.length && !isFetchingData ? (
          <TeamEmptyWarningModal
            linkToWorkspacesPage={`/${organization?.slug}/workspaces/${workspace?.slug}/details`}
            workspaceTitle={String(workspace?.name)}
          />
        ) : null}

        <p className="assignedTo">{job.assigneeId ? 'Currently assigned to' : 'Currently not assigned'}</p>
        {renderAssignee()}
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />

        <TeamsFilter
          teamsFilterValues={teamsFilterValues}
          chosenTeamsFilter={chosenTeamsFilter}
          setChosenTeamsFilter={setChosenTeamsFilter}
          isOrganizationTeams={userHasManageMembersPermissionForCurrentWorkspace}
        />

        <TableWrapper className="customTable">
          {isFetchingData ? <AbsoluteSpinner overlay className="mt-4 h-full" /> : null}
          <Table>
            <Thead $white>
              <Tr>
                <Th style={{ width: '1%' }}>Name</Th>
                <Th>Email</Th>
                <Th style={{ width: '1%' }}>Teams</Th>
              </Tr>
            </Thead>
            {!isFetchingData ? (
              <Tbody>
                {users.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <p className="pr-2 cursor-pointer" onClick={() => changeAssignee(data)}>
                        {data?.name} {data?.lastname}
                      </p>
                    </Td>
                    <Td>
                      <p
                        className="max-w-full overflow-hidden text-ellipsis px-2 cursor-pointer"
                        onClick={() => changeAssignee(data)}
                      >
                        {data?.email}
                      </p>
                    </Td>
                    <Td>
                      <div className="flex">
                        {data?.teams?.map((team) => (
                          <TeamTag
                            key={team.id}
                            team={team}
                            active={!chosenTeamsFilter.length || chosenTeamsFilter.includes(team.slug)}
                            onClick={() => changeAssignee(data)}
                          />
                        ))}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            ) : null}
          </Table>
        </TableWrapper>
        {canChangeStatus ? (
          <>
            <p className="text pb-2">Set status</p>
            {renderStatusRadioList()}
          </>
        ) : null}

        <p className="assignee-comment">Add a comment for assignee</p>
        <StyledTextarea
          label="Type your message here"
          name="assigneeComment"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
          value={comment}
        />

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={handleSubmit}>Save</Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
